import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';

export function isInSavedSearchScope(ss, isShared, userId) {
  if (isShared) return Boolean(ss.teamId);
  return !ss.teamId && String(ss.creatorId) === String(userId);
}

/**
 * Canonical path for a segment (group or saved search name) under a parent group.
 * Root-level parent uses only the leading delimiter + segment (matches DB / moveToGroup).
 */
export function fullPathUnderParent(parentPath, segmentName) {
  const seg = (segmentName || '').trim();
  const base = parentPath || '';
  if (!base) return `${NEW_PATH_DELIMITER}${seg}`;
  return `${base}${NEW_PATH_DELIMITER}${seg}`;
}

export function hasConflictAtFullPath(all, {
  fullPath, isShared, userId, excludeId,
}) {
  if (!fullPath) return false;
  const fp = fullPath.toLowerCase();
  return all.some((ss) => {
    if (!ss || !ss.path) return false;
    if (excludeId && String(ss._id) === String(excludeId)) return false;
    if (!isInSavedSearchScope(ss, isShared, userId)) return false;
    return ss.path.toLowerCase() === fp;
  });
}

/** Ungrouped saved searches have no `path` field; uniqueness is by name per scope. */
export function hasUngroupedNameConflict(all, {
  name, isShared, userId, excludeId,
}) {
  const n = (name || '').trim();
  if (!n) return false;
  const nLower = n.toLowerCase();
  return all.some((ss) => {
    if (!ss) return false;
    if (excludeId && String(ss._id) === String(excludeId)) return false;
    if (!isInSavedSearchScope(ss, isShared, userId)) return false;
    if (ss.path) return false;
    return (ss.name || '').trim().toLowerCase() === nLower;
  });
}

export function conflictForNewSavedSearch(all, userId, {
  parentPath, name, isShared,
}) {
  const n = (name || '').trim();
  if (!n) return false;
  if (!parentPath) {
    return hasUngroupedNameConflict(all, {
      name: n, isShared, userId, excludeId: null,
    });
  }
  const fp = fullPathUnderParent(parentPath, n);
  return hasConflictAtFullPath(all, {
    fullPath: fp, isShared, userId, excludeId: null,
  });
}

export function conflictForNewGroup(all, userId, {
  parentPath, groupName, isShared,
}) {
  const fp = fullPathUnderParent(parentPath || '', groupName);
  return hasConflictAtFullPath(all, {
    fullPath: fp, isShared, userId, excludeId: null,
  });
}

export function conflictForMoveSavedSearch(all, userId, {
  targetParentPath, itemName, isShared, excludeId,
}) {
  const n = (itemName || '').trim();
  if (!n) return false;
  if (!(targetParentPath || '')) {
    return hasUngroupedNameConflict(all, {
      name: n, isShared, userId, excludeId,
    });
  }
  const fp = fullPathUnderParent(targetParentPath, n);
  return hasConflictAtFullPath(all, {
    fullPath: fp, isShared, userId, excludeId,
  });
}

function isPathInSubtreeBeingMoved(ssPath, subtreeRootPath) {
  if (!subtreeRootPath || !ssPath) return false;
  if (ssPath === subtreeRootPath) return true;
  return ssPath.startsWith(subtreeRootPath + NEW_PATH_DELIMITER);
}

/** Destination path conflict when moving a group + subtree (exclude every doc under the moved root). */
export function conflictForMoveSavedSearchGroup(all, userId, {
  targetParentPath, groupName, isShared, subtreeRootPath,
}) {
  const fp = fullPathUnderParent(targetParentPath || '', groupName);
  const fpLower = fp.toLowerCase();
  return all.some((ss) => {
    if (!ss || !ss.path) return false;
    if (!isInSavedSearchScope(ss, isShared, userId)) return false;
    if (isPathInSubtreeBeingMoved(ss.path, subtreeRootPath)) return false;
    return ss.path.toLowerCase() === fpLower;
  });
}

/**
 * Sibling segment names at one tree level (groups + saved searches share one namespace).
 */
export function collectSiblingSegmentNames(nodes) {
  if (!Array.isArray(nodes)) return [];
  const names = [];
  nodes.forEach((n) => {
    if (!n) return;
    names.push(n.name);
  });
  return names;
}
